/** @file Command-pattern history (undo/redo). */

/**
 * @typedef {object} Command
 * @property {string} type
 * @property {() => void} do
 * @property {() => void} undo
 */

const MAX_HISTORY = 50;

/** @type {Command[]} */
let past = [];
/** @type {Command[]} */
let future = [];

/** @type {Set<() => void>} */
const listeners = new Set();

/**
 * Execute a command and push it to history. Clears redo stack.
 * @param {Command} cmd
 */
export function execute(cmd) {
  cmd.do();
  past.push(cmd);
  if (past.length > MAX_HISTORY) {
    past.shift();
  }
  future.length = 0;
  notify();
}

/** Undo the most recent command. @returns {boolean} whether an undo happened */
export function undo() {
  const cmd = past.pop();
  if (!cmd) {
    return false;
  }
  cmd.undo();
  future.push(cmd);
  notify();
  return true;
}

/** Redo the most recently undone command. @returns {boolean} */
export function redo() {
  const cmd = future.pop();
  if (!cmd) {
    return false;
  }
  cmd.do();
  past.push(cmd);
  notify();
  return true;
}

export function canUndo() {
  return past.length > 0;
}

export function canRedo() {
  return future.length > 0;
}

export function clear() {
  past = [];
  future = [];
  notify();
}

/** Alias for `clear()` — used on play-load to discard undo history across plays. */
export function historyReset() {
  clear();
}

/**
 * @param {() => void} fn
 * @returns {() => void}
 */
export function onChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  for (const fn of listeners) {
    fn();
  }
}

/** For tests only. */
export function __reset() {
  past = [];
  future = [];
  listeners.clear();
}

/** For tests. */
export function __pastLength() {
  return past.length;
}

/** For tests. */
export function __futureLength() {
  return future.length;
}
